import React, { useEffect, useState } from 'react';
import ApiContext from './ApiContext';
import VideoThumbnail from './VideoThumbnail';
import LoadingCircle from './LoadingCircle';

const allTags = ['Taucher', 'Freitaucher', 'Tauchdrohne', 'Karpfen', 'Hecht', 'Goldfisch', 'Flussbarsch', 'Sonnenbarsch', 'Rotauge', 'Karausche', 'Flusskrebs', 'Wels', 'Kormoran', '★★★'];

const VideoGallery = () => {
    const { baseUrl } = React.useContext(ApiContext);
    const [groupedVideos, setGroupedVideos] = useState({});
    const [currentVideo, setCurrentVideo] = useState(null);    
    const [isOverlayVisible, setIsOverlayVisible] = useState(false);
    const [activeTags, setActiveTags] = useState([]);

    const [selectedTagFilter, setSelectedTagFilter] = useState(null);
    const [filterTags, setFilterTags] = useState([]);

    const [loading, setLoading] = useState(false);
    const [loadingTimeoutId, setLoadingTimeoutId] = useState(null);

    const playVideo = (video) => {
        setCurrentVideo(video);
        setActiveTags(video.tags || []); // Setzt die aktiven Tags, wenn ein Video gespielt wird
        setIsOverlayVisible(true);
    };

    const closeVideo = () => {
        if (currentVideo) {
            updateTagsInDatabase(currentVideo.filename, activeTags);
        }
        setIsOverlayVisible(false);
        window.history.pushState({}, '', window.location.origin);
    };    

    const toggleTag = (tag) => {
        setActiveTags((prevTags) => {
            if (prevTags.includes(tag)) {
                return prevTags.filter((t) => t !== tag); // Tag entfernen
            } else {
                return [...prevTags, tag]; // Tag hinzufügen
            }
        });
    };

    const checkIfTagIsActive = (tag) => {
        return activeTags.includes(tag);
    };

    const fetchAndAttachTags = (videos) => {
    fetch(`${baseUrl}/get-tags`)
        .then(response => {
            if (response.ok) return response.json();
            else throw new Error('Failed to fetch tags');
        })
        .then(tagsData => {
            const updatedVideos = { ...videos };
            
            // Für jedes Video aus dem Tagging-Service die Tags zu den Videos hinzufügen
            tagsData.forEach(tagInfo => {
                for (const date in updatedVideos) {
                    const video = updatedVideos[date].find(v => v.filename === tagInfo.filename);
                    if (video) {
                        video.tags = tagInfo.tags;
                    }
                }
            });
            
            setGroupedVideos(updatedVideos);
        })
        .catch(error => console.error('Error fetching tags:', error));
    };

    // Funktion zum Hinzufügen oder Entfernen eines Tags vom Filter
    const toggleFilterTag = (tag) => {
        setFilterTags(prevTags => {
            if (prevTags.includes(tag)) {
                // Tag entfernen
                return prevTags.filter(t => t !== tag);
            } else {
                // Tag hinzufügen
                return [...prevTags, tag];
            }
        });
    };

    // Prüfen, ob ein Video den Filterkriterien entspricht
    const videoMatchesFilter = (video) => {
        // Wenn keine Filtertags ausgewählt sind, alle Videos anzeigen
        if (filterTags.length === 0) {
            return true;
        }
        // Überprüfen, ob das Video alle Tags hat, die im Filter enthalten sind (logisches Und)
        return video.tags && filterTags.every(tag => video.tags.includes(tag));
    };    

    // Filterfunktion für Videos nach Tag
    const filterVideosByTag = (tag) => {
        if (!tag) {
            // Wenn kein Tag ausgewählt ist, zeigen Sie alle Videos an
            return groupedVideos;
        }
        // Filtern Sie die Videos für jeden Tag
        const filteredVideos = {};
        for (const date in groupedVideos) {
            filteredVideos[date] = groupedVideos[date].filter((video) =>
                video.tags && video.tags.includes(tag)
            );
        }
        return filteredVideos;
    };

    // Klicken Sie auf Handler für Tag-Filter-Buttons
    const handleTagFilterClick = (tag) => {
        setSelectedTagFilter(tag === selectedTagFilter ? null : tag); // Tag umschalten oder Filter löschen
    };

    // Gefilterte Videos basierend auf dem ausgewählten Tag-Filter
    const filteredGroupedVideos = filterVideosByTag(selectedTagFilter);

    const fetchVideos = (retries = 3, delay = 2000) => {

        setLoading(true);

        fetch('https://riesenstein-bucket.s3.eu-west-1.amazonaws.com/list-videos.json', {
            cache: 'no-store'
        })
            .then(response => {
                if (response.ok) return response.json();
                else throw new Error('API request failed');
            })
            .then(data => {

                clearTimeout(loadingTimeoutId);
                setLoading(false);

                setGroupedVideos(data.videos);
                fetchAndAttachTags(data.videos);
                
                const params = new URLSearchParams(window.location.search);
                const videoFilename = params.get('findVideo');

                for(let date in data.videos) {
                    const video = data.videos[date].find(v => v.filename === videoFilename);
                    if(video) {
                        playVideo(video);
                        const videoThumbnailElement = document.querySelector(`.thumbnail[data-filename="${videoFilename}"]`);
                        if (videoThumbnailElement) {
                            videoThumbnailElement.scrollIntoView({
                                behavior: 'smooth'
                            });
                        }
                        break;
                    }
                }                
            })
            .catch(error => {
                console.error('Error fetching videos:', error);
                if (retries > 0) {
                    setTimeout(() => {
                        fetchVideos(retries - 1, delay);
                    }, delay);
                } else {
                    console.error('Failed to fetch videos after multiple attempts');
                }

                clearTimeout(loadingTimeoutId);
                setLoading(false);
            });
    };

    const updateTagsInDatabase = (videoFilename, tags) => {
        const url = `${baseUrl}/set-tags`;

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                filename: videoFilename,
                tags: tags,
            }),
        })
        .then(response => {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(data => {
            console.log('Tags successfully updated:', data);
            // Aktualisieren Sie den lokalen Zustand, um die Änderungen widerzuspiegeln
            setGroupedVideos(prevGroupedVideos => {
                const newGroupedVideos = { ...prevGroupedVideos };
                for (const date in newGroupedVideos) {
                    const videoIndex = newGroupedVideos[date].findIndex(v => v.filename === videoFilename);
                    if (videoIndex !== -1) {
                        newGroupedVideos[date][videoIndex] = { ...newGroupedVideos[date][videoIndex], tags };
                        break; // Wenn das Video gefunden und aktualisiert wurde, brechen Sie die Schleife ab
                    }
                }
                return newGroupedVideos;
            });
        })
        .catch((error) => {
            console.error('Error updating tags:', error);
        });
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape" && isOverlayVisible) {
                closeVideo();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOverlayVisible]);


    useEffect(() => {
        fetchVideos();
    }, []);

    useEffect(() => {
        // Setzen Sie alle Tags des aktuellen Videos als aktiv
        if (currentVideo && currentVideo.tags) {
            setActiveTags(currentVideo.tags);
        } else {
            setActiveTags([]);
        }
    }, [currentVideo]);

    return (
        <div className="video-gallery">            
            <div className="tag-filter-buttons">
                {allTags.map((tag) => (
                    <button
                        key={tag}
                        onClick={() => toggleFilterTag(tag)}
                        className={filterTags.includes(tag) ? 'active' : ''}
                    >
                        {tag}
                    </button>
                ))}
            </div>
            {loading && <LoadingCircle />}
            {Object.keys(groupedVideos)
                .sort((a, b) => Date.parse(b) - Date.parse(a))
                .map((date) => {
                    const filteredVideos = groupedVideos[date].filter(videoMatchesFilter);
                    // Überprüfen, ob gefilterte Videos vorhanden sind, bevor wir die Gruppe rendern
                    if (filteredVideos.length > 0) {
                        return (
                            <div key={date} className="date-group">
                                <h2>Videos vom {date}:</h2>
                                <div className="video-group">
                                    {filteredVideos.map((video, vIndex) => (
                                        <VideoThumbnail key={vIndex} video={video} onPlay={playVideo} />
                                    ))}
                                </div>
                            </div>
                        );
                    }
                    return null; // Keine Videos vorhanden, daher wird nichts gerendert
                })}
            {isOverlayVisible && currentVideo && (
                <div className="video-overlay visible">
                    <div className="video-wrapper">                        
                        <div className="video-middle-column">
                            <div className="video-container">
                                <video controls src={currentVideo.videoUrl} autoPlay></video>
                                <div className="video-container-tags">
                                    {/* Alle verfügbaren Tags anzeigen und basierend auf currentVideo als aktiv oder inaktiv kennzeichnen */}
                                    {allTags.map((tag) => (
                                        <span
                                            key={tag}
                                            className={`video-tag ${checkIfTagIsActive(tag) ? 'active' : 'inactive'}`}
                                            onClick={() => toggleTag(tag)}
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <button className="close-button" onClick={closeVideo}>x</button>
                </div>
            )}
        </div>
    );
};

export default VideoGallery;